import { OpenloginUserInfo } from '@web3auth/openlogin-adapter'
import { createContext, FunctionComponent, ReactNode, useContext, useMemo, useState } from 'react'
// import { useConnect } from 'wagmi'
import { useLocalStorage } from '@mantine/hooks'
import { web3AuthInstance } from '@/App'

export interface IWeb3AuthContext {
  isLoading: boolean
  userInfo: Partial<OpenloginUserInfo> | null
  login: () => Promise<any>
  logout: () => Promise<void>
  getUserInfo: () => Promise<Partial<OpenloginUserInfo>>
}

export const Web3AuthContext = createContext<IWeb3AuthContext>({
  isLoading: false,
  userInfo: null,
  login: async () => {},
  logout: async () => {},
  getUserInfo: async () => Promise.resolve({}),
})

export function useWeb3Auth(): IWeb3AuthContext {
  return useContext(Web3AuthContext)
}
interface IWeb3AuthProps {
  children: ReactNode
}

export const Web3AuthProvider: FunctionComponent<IWeb3AuthProps> = ({
  children,
}: IWeb3AuthProps) => {
  const [userInfo, setUserInfo] = useLocalStorage<Partial<OpenloginUserInfo> | null>({
    key: 'userInfo',
    defaultValue: null,
  })
  const [isLoading, setIsLoading] = useState(false)
  const provider = web3AuthInstance

  // Not able to properly get web3auth provider for some reason
  // // Wagmi Provider (Web3Auth Connector)
  // const { connectors } = useConnect()
  // const getWagmiProvider = async () => {
  //   console.log('connectors', connectors)
  //   const provider = await connectors[0]?.getProvider()
  //   if (!provider) throw new Error('Provider not found')
  //   return provider
  // }

  /* -------------------------------------------------------------------------- */
  /*                             Web3Auth Functions                             */
  /* -------------------------------------------------------------------------- */
  const getUserInfo = async (): Promise<Partial<OpenloginUserInfo>> => {
    // const provider = await getWagmiProvider()
    if (!provider) {
      console.warn('[Web3Auth] web3auth not initialized yet')
      return {} as OpenloginUserInfo
    }
    const newUserInfo = await provider.getUserInfo()
    setUserInfo(newUserInfo)
    return newUserInfo
  }

  const login = async () => {
    // const provider = await getWagmiProvider()
    if (!provider) {
      console.warn('[Web3Auth] web3auth not initialized yet')
      return null
    }
    setIsLoading(true)
    const localProvider = await provider.connect()
    if (!localProvider) {
      console.warn('[Web3Auth] localProvider not initialized yet')
      return null
    }
    // Retrieve user information to ensure the correct data is displayed for the user on login
    const info = await getUserInfo()
    setIsLoading(false)
    return info
  }

  const logout = async () => {
    // const provider = await getWagmiProvider()
    if (!provider?.connected) {
      console.warn('[Web3Auth] web3auth not initialized yet')
      return
    }
    await provider.logout()
  }
  /* ------------------------------------ - ----------------------------------- */

  const contextProvider = useMemo(
    () => ({
      userInfo,
      isLoading,
      login,
      logout,
      getUserInfo,
    }),
    [userInfo, isLoading, login, logout, getUserInfo]
  )

  return <Web3AuthContext.Provider value={contextProvider}>{children}</Web3AuthContext.Provider>
}
